/**
 * Copy for the "Mark this project complete?" confirmation, shared by the
 * Projects page and the account page so the two can't drift apart again.
 *
 * Closing a project is not just a status flip — since v1.5.39 `PUT /api/projects/:id`
 * completes every remaining open task (root AND sub-tasks) and fires the recurrence
 * spawn. Both dialogs described only the status flip: the account page never
 * mentioned tasks at all, and the Projects page counted only the tasks the row
 * happened to have loaded, so closing a COLLAPSED project — the case that used to
 * complete nothing and now completes everything — showed no warning at all.
 *
 * Counts come from the server aggregates on `GET /api/projects`
 * (`server/lib/projectTaskAggregate.ts`), which deliberately include sub-tasks and
 * skip tasks under a deleted parent. That's the same source as the row's progress
 * bar, so the dialog can never disagree with the bar the user is looking at.
 */

export interface ProjectCloseWarningInput {
  firm_task_total?: number;
  firm_task_done?: number;
  client_task_total?: number;
  client_task_done?: number;
  recurring_pattern?: string | null;
  next_spawn_date?: string | null;
  /**
   * Open counts the caller knows better than the cached aggregates — used by the
   * auto-close prompt, which fires immediately after a task was completed and so
   * would otherwise still count that task as open. An explicit 0 means
   * known-empty, not unknown.
   */
  openTaskCount?: number;
  openClientTaskCount?: number;
}

export interface ProjectCloseWarning {
  /** Open tasks incl. sub-tasks, or null when the row carries no aggregates. */
  openTaskCount: number | null;
  openClientTaskCount: number | null;
  willSpawn: boolean;
  /** Sentences to render after "<title> will be closed." */
  lines: string[];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Formats a stored `YYYY-MM-DD` without going through Date — `new Date("2026-01-01")`
 * is UTC midnight, which renders as Dec 31 in Eastern (the repo's date-drift trap).
 * Anything that isn't a plain date string returns null so the caller drops the date
 * rather than printing something wrong.
 */
function formatSpawnDate(value: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!m) return null;
  const [, y, mo, d] = m;
  const monthIndex = Number(mo) - 1;
  if (monthIndex < 0 || monthIndex > 11) return null;
  return `${MONTHS[monthIndex]} ${Number(d)}, ${y}`;
}

const openCount = (total?: number, done?: number): number => Math.max(0, (total ?? 0) - (done ?? 0));

export function buildProjectCloseWarning(project: ProjectCloseWarningInput): ProjectCloseWarning {
  const explicit = project.openTaskCount !== undefined;
  const hasAggregates = explicit
    || project.firm_task_total !== undefined
    || project.client_task_total !== undefined;

  const openTotal = explicit
    ? Math.max(0, project.openTaskCount ?? 0)
    : openCount(project.firm_task_total, project.firm_task_done)
      + openCount(project.client_task_total, project.client_task_done);
  const openClient = explicit
    ? Math.min(openTotal, Math.max(0, project.openClientTaskCount ?? 0))
    : openCount(project.client_task_total, project.client_task_done);

  // A clone inherits recurring_pattern, so the pattern alone means nothing — the
  // server only spawns while next_spawn_date is set (see the atomic claim in
  // checkAndSpawnOnCompletion). Requiring both keeps this from promising a
  // successor for a chain that has been stopped.
  const willSpawn = !!project.recurring_pattern && !!project.next_spawn_date;

  const lines: string[] = [];

  if (!hasAggregates) {
    lines.push("Any open tasks on it will also be marked done.");
  } else if (openTotal > 0) {
    const plural = openTotal === 1 ? "task" : "tasks";
    if (openClient === openTotal) {
      lines.push(`Its ${openTotal} open client ${plural} will also be marked done.`);
    } else if (openClient > 0) {
      lines.push(`Its ${openTotal} open ${plural} — ${openClient} of them client tasks — will also be marked done.`);
    } else {
      lines.push(`Its ${openTotal} open ${plural} will also be marked done.`);
    }
  }

  if (willSpawn) {
    const when = formatSpawnDate(project.next_spawn_date);
    lines.push(
      when
        ? `This project recurs, so closing it creates the next occurrence (${when}).`
        : "This project recurs, so closing it creates the next occurrence.",
    );
  }

  // Reopening deliberately does NOT reopen tasks (we can't tell which were genuinely
  // done before the close), so the usual "you can undo this" reassurance would be a
  // half-truth wherever tasks are actually being completed.
  const completesSomething = !hasAggregates || openTotal > 0;
  lines.push(
    completesSomething
      ? "You can reopen the project later, but those tasks will stay done."
      : "You can reopen it later from the same checkbox.",
  );

  return {
    openTaskCount: hasAggregates ? openTotal : null,
    openClientTaskCount: hasAggregates ? openClient : null,
    willSpawn,
    lines,
  };
}
